import { z } from "zod";

export const ExperimentCategorySchema = z.enum([
  "connection",
  "movement",
  "recovery",
  "gratitude",
  "meaning",
  "presence",
  "other",
]);
export type ExperimentCategory = z.infer<typeof ExperimentCategorySchema>;

export const ExperimentSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  category: ExperimentCategorySchema,
  difficulty: z.number().int().min(1).max(5),
  durationMinutes: z.number().int().positive(),
  minDurationMinutes: z.number().int().positive(),
  premium: z.boolean().default(false),
  title: z.string(),
  summary: z.string(),
  rationale: z.string(),
  instructions: z.array(z.string()).min(1).max(5),
  smallerVersion: z.string(),
  reflectionQuestions: z.array(z.string()).max(4),
});

export type Experiment = z.infer<typeof ExperimentSchema>;

export const AssignmentStatusSchema = z.enum([
  "assigned",
  "started",
  "completed",
  "skipped",
]);
export type AssignmentStatus = z.infer<typeof AssignmentStatusSchema>;

export const ContentOverrideSchema = z.object({
  title: z.string().optional(),
  summary: z.string().optional(),
  rationale: z.string().optional(),
  instructions: z.array(z.string()).optional(),
  smallerVersion: z.string().optional(),
  reflectionQuestions: z.array(z.string()).optional(),
  durationMinutes: z.number().int().positive().optional(),
  difficulty: z.number().int().min(1).max(5).optional(),
  category: ExperimentCategorySchema.optional(),
});
export type ContentOverride = z.infer<typeof ContentOverrideSchema>;

export const ExperimentAssignmentSchema = z.object({
  id: z.string().uuid(),
  experimentId: z.string().uuid(),
  status: AssignmentStatusSchema,
  recommendationReason: z.string(),
  scheduledFor: z.string().datetime().nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  contentOverride: ContentOverrideSchema.nullable().optional(),
});

export type ExperimentAssignment = z.infer<typeof ExperimentAssignmentSchema>;

export const ExperimentOutcomeSchema = z.object({
  assignmentId: z.string().uuid(),
  completed: z.boolean(),
  afterMood: z.number().int().min(0).max(4).nullable(),
  afterEnergy: z.number().int().min(0).max(10).nullable(),
  repeatIntent: z.number().int().min(0).max(2).nullable(), // 0=no, 1=maybe, 2=yes
  reflection: z.string().max(1000).optional(),
});

export type ExperimentOutcome = z.infer<typeof ExperimentOutcomeSchema>;
